// components
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';

// queries
import { queries } from 'RestAPIs/rapini';

// utils
import {
  getImportSyncStatusRowId,
  ImportSyncStatusLogRow,
  useColumnDefinitions
} from './ImportSynchronisationLogs.utils';
import {
  computeRange,
  toDate
} from 'components/shared/DateNavigation/DateNavigation.utils';

// styles
import {
  DateNavContainer,
  LogsPageContainer,
  SynchronisationLogsDateNavigationContainer,
  SynchronisationLogsFiltersContainer,
  SynchronisationLogsRefreshContainer,
  SynchronisationLogsRefreshButton
} from 'components/logs/logs.styles';

// types
import { DateNavigation } from 'components/shared/index';

// libraries
import { Tooltip } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@sdb/planning-ui';

// stores
import { useDateNavigationHoursStore } from 'components/shared/DateNavigation/DateNavigationHours.stores';
import { useShallow } from 'zustand/shallow';
import { useDateNavigationStore } from 'components/shared/DateNavigation/DateNavigation.stores';
import { SingleOrRange } from 'components/shared/DateNavigation';
import AppointmentSummary from 'components/logs/components/SynchronisationLogs/components/AppointmentSummary/AppointmentSummary';

const ImportSynchronisationLogs = () => {
  const { t } = useTranslation('common');
  const { selectedDate, setSelectedDate } = useDateNavigationHoursStore(
    useShallow((state) => ({
      selectedDate: state.selectedDate,
      setSelectedDate: state.setSelectedDate
    }))
  );

  const period = useDateNavigationStore(useShallow((state) => state.period));
  const appliedPeriod = period ?? 'day';

  const { startDate, endDate } = useMemo(() => {
    return computeRange(selectedDate, appliedPeriod);
  }, [appliedPeriod, selectedDate]);

  const {
    data: importSyncLogs,
    isLoading,
    isError,
    refetch
  } = queries.useGetImportAppointmentsSyncStatuses(
    startDate,
    endDate,
    undefined
  );

  const handleSelectedDateChange = useCallback<(date: SingleOrRange) => void>(
    (date) => {
      setSelectedDate(Array.isArray(date) || !date ? null : toDate(date));
    },
    [setSelectedDate]
  );

  const columns = useColumnDefinitions();
  const rows = (importSyncLogs || []) as ImportSyncStatusLogRow[];
  const refreshLabel = t('logsPortal.importSynchronisationLogs.refresh');

  return (
    <LogsPageContainer>
      <DateNavContainer>
        <SynchronisationLogsFiltersContainer>
          <div />

          <SynchronisationLogsDateNavigationContainer>
            <DateNavigation
              allowMenuOptions
              defaultDate={selectedDate}
              defaultPeriod="day"
              onDateChange={handleSelectedDateChange}
              options={['day', 'week']}
            />
          </SynchronisationLogsDateNavigationContainer>

          <SynchronisationLogsRefreshContainer>
            <Tooltip title={refreshLabel}>
              <SynchronisationLogsRefreshButton
                aria-label={refreshLabel}
                onClick={() => {
                  void refetch();
                }}
              >
                <Icon type="arrow-rotate-right" fontSize="small" />
              </SynchronisationLogsRefreshButton>
            </Tooltip>
          </SynchronisationLogsRefreshContainer>
        </SynchronisationLogsFiltersContainer>
      </DateNavContainer>

      <TableCRUDLayout
        loading={isLoading}
        columns={columns}
        initialState={{
          sorting: {
            sortModel: [
              {
                field: 'appointmentDate',
                sort: 'asc'
              }
            ]
          }
        }}
        onClose={refetch}
        rows={rows}
        slots={{
          row: (gridRow) => (
            <AppointmentSummary
              appointmentSummary={
                gridRow.row.appointmentContext?.appointmentSummary
              }
              gridRow={gridRow}
            />
          )
        }}
        getRowId={(log) =>
          getImportSyncStatusRowId(log as ImportSyncStatusLogRow)
        }
        hasLoadingError={isError}
        onLoadingError={refetch}
        disableActions
      />
    </LogsPageContainer>
  );
};

export default ImportSynchronisationLogs;
